/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ObservableList, ObservableMap, ObserveModes } from '@typescript-kit/core';

import { Employee } from '@teamworks/global';
import { CommentModel } from '@teamworks/global';
import { TimeRecordModel } from '@teamworks/global';
import { PublicHoliday } from '@teamworks/global';
import { CarryoverModel } from '@teamworks/global';
import { TimeRecordingSummary } from '@teamworks/global';

import { Project } from '@teamworks/global';
import { MonthSelectViewData, MonthSelectViewFieldKey, MonthSelectViewModel } from '../../shared/view/month-select.view-model';
import { WorkingHoursRecordModel } from '@teamworks/global';
import { TargetHoursModel } from '@teamworks/global';

export interface TimeRecordingViewData extends MonthSelectViewData {
  printView?: boolean;
  employeeList?: Employee[];
  employeeId?: string;
  projectList?: Project[];
  projectId?: string;
  monthComment?: CommentModel;
  timeRecordList?: Iterable<TimeRecordModel>;
  targetHoursMap?: Iterable<TargetHoursModel>;
  publicHolidayList?: PublicHoliday[];
  defaultWorkingHoursRecordList?: Iterable<WorkingHoursRecordModel>;
  carryover?: CarryoverModel;
  summary?: TimeRecordingSummary;
  isProjectAdmin?: boolean;
}

export class TimeRecordingViewFieldKey extends MonthSelectViewFieldKey {
  static readonly PRINT_VIEW = 'printView';
  static readonly EMPLOYEE_LIST = 'employeeList';
  static readonly EMPLOYEE_ID = 'employeeId';
  static readonly PROJECT_LIST = 'projectList';
  static readonly PROJECT_ID = 'projectId';
  static readonly MONTH_COMMENT = 'monthComment';
  static readonly TIME_RECORD_LIST = 'timeRecordList';
  static readonly TARGET_HOURS_MAP = 'targetHoursMap';
  static readonly PUBLIC_HOLIDAY_LIST = 'publicHolidayList';
  static readonly DEFAULT_WORKING_HOURS_RECORD_LIST = 'defaultWorkingHoursRecordList';
  static readonly CARRYOVER = 'carryover';
  static readonly SUMMARY = 'summary';
  static readonly IS_PROJECT_ADMIN = 'isProjectAdmin';
}

export class TimeRecordingViewModel extends MonthSelectViewModel implements TimeRecordingViewData {
  static readonly KEYS = MonthSelectViewModel.KEYS.concat(
    Object.keys(TimeRecordingViewFieldKey).map((key) => TimeRecordingViewFieldKey[key])
  );

  constructor(data?: TimeRecordingViewData) {
    super(data, TimeRecordingViewModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get printView(): boolean {
    return this.get(TimeRecordingViewFieldKey.PRINT_VIEW);
  }

  set printView(value: boolean) {
    this.set(TimeRecordingViewFieldKey.PRINT_VIEW, value);
  }

  get employeeList(): Employee[] {
    return this.get(TimeRecordingViewFieldKey.EMPLOYEE_LIST);
  }

  set employeeList(value: Employee[]) {
    this.set(TimeRecordingViewFieldKey.EMPLOYEE_LIST, value);
  }

  get employeeId(): string {
    return this.get(TimeRecordingViewFieldKey.EMPLOYEE_ID);
  }

  set employeeId(value: string) {
    this.set(TimeRecordingViewFieldKey.EMPLOYEE_ID, value);
  }

  get projectList(): Project[] {
    return this.get(TimeRecordingViewFieldKey.PROJECT_LIST);
  }

  set projectList(value: Project[]) {
    this.set(TimeRecordingViewFieldKey.PROJECT_LIST, value);
  }

  get projectId(): string {
    return this.get(TimeRecordingViewFieldKey.PROJECT_ID);
  }

  set projectId(value: string) {
    this.set(TimeRecordingViewFieldKey.PROJECT_ID, value);
  }

  get monthComment(): CommentModel {
    return this.get(TimeRecordingViewFieldKey.MONTH_COMMENT);
  }

  set monthComment(value: CommentModel) {
    this.set(TimeRecordingViewFieldKey.MONTH_COMMENT, value);
  }

  get timeRecordList(): ObservableList<TimeRecordModel> {
    return this.get(TimeRecordingViewFieldKey.TIME_RECORD_LIST);
  }

  set timeRecordList(value: ObservableList<TimeRecordModel>) {
    this.set(TimeRecordingViewFieldKey.TIME_RECORD_LIST, value);
  }

  get targetHoursMap(): ObservableMap<TargetHoursModel> {
    return this.get(TimeRecordingViewFieldKey.TARGET_HOURS_MAP);
  }

  set targetHoursMap(value: ObservableMap<TargetHoursModel>) {
    this.set(TimeRecordingViewFieldKey.TARGET_HOURS_MAP, value);
  }

  get publicHolidayList(): PublicHoliday[] {
    return this.get(TimeRecordingViewFieldKey.PUBLIC_HOLIDAY_LIST);
  }

  set publicHolidayList(value: PublicHoliday[]) {
    this.set(TimeRecordingViewFieldKey.PUBLIC_HOLIDAY_LIST, value);
  }

  get defaultWorkingHoursRecordList(): ObservableList<WorkingHoursRecordModel> {
    return this.get(TimeRecordingViewFieldKey.DEFAULT_WORKING_HOURS_RECORD_LIST);
  }

  set defaultWorkingHoursRecordList(value: ObservableList<WorkingHoursRecordModel>) {
    this.set(TimeRecordingViewFieldKey.DEFAULT_WORKING_HOURS_RECORD_LIST, value);
  }

  get carryover(): CarryoverModel {
    return this.get(TimeRecordingViewFieldKey.CARRYOVER);
  }

  set carryover(value: CarryoverModel) {
    this.set(TimeRecordingViewFieldKey.CARRYOVER, value);
  }

  get summary(): TimeRecordingSummary {
    return this.get(TimeRecordingViewFieldKey.SUMMARY);
  }

  set summary(value: TimeRecordingSummary) {
    this.set(TimeRecordingViewFieldKey.SUMMARY, value);
  }

  get isProjectAdmin(): boolean {
    return this.get(TimeRecordingViewFieldKey.IS_PROJECT_ADMIN);
  }

  set isProjectAdmin(value: boolean) {
    this.set(TimeRecordingViewFieldKey.IS_PROJECT_ADMIN, value);
  }

  protected setupValues(values: TimeRecordingViewData, exclusive: boolean): TimeRecordingViewData {
    if (exclusive) {
      if (!(TimeRecordingViewFieldKey.PRINT_VIEW in values)) {
        values.printView = false;
      }
    }
    return super.setupValues(values, exclusive) as TimeRecordingViewData;
  }

}
